/**
 * Capturing what a chart looks like, so the catalog can show it.
 *
 * The picture is taken in the browser, at the one moment it is free: the chart
 * is already drawn on screen when someone saves it. The alternatives are worse
 * in specific ways - rendering server-side needs a headless browser in the API
 * path, and drawing a live preview per catalog card costs one `POST /plot` per
 * card against a backend with a single shared DuckDB connection, which is the
 * thing `tile-fetch.ts`'s queue exists to prevent.
 *
 * What this produces is deliberately small: 480x270 is enough to recognise a
 * shape at card size and small enough to store in Postgres. It is a *preview*,
 * not an export - `image-export.ts`
 * remains the full-fidelity path, legends and all.
 *
 * A capture failing is never worth failing a save over. Every path here returns
 * null and the card falls back to its plot-type glyph, which is also what
 * happens for a chart saved without plotting first.
 */

import { putChartThumbnail } from '../../api/endpoints.ts';
import { runtime } from '../plot/plot-runtime.ts';
import { chartLayout } from '../plot/plot-ui-state.ts';

/** Card aspect: wide enough to read a time series, short enough to tile. */
export const THUMBNAIL_WIDTH = 480;
export const THUMBNAIL_HEIGHT = 270;

/**
 * The chart currently on screen, as a PNG data URL, or null when there is none.
 *
 * Reads the canvas owned by the active chart, or composes the canvases and ship
 * labels in its Grid host. Neither path searches the rest of the document:
 * dashboard tiles and editor previews may have other canvases on the page.
 */
export function captureThumbnail(): string | null {
  return captureThumbnailAt(THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);
}

/**
 * The same capture at a chosen size and encoding.
 *
 * Home's recent-views strip keeps its pictures in `localStorage`, where a 40 KB
 * PNG each is not affordable - so it asks for a small JPEG instead. Everything
 * else about the capture (the ground fill, the contain-fit, the null on
 * failure) is identical, which is why this is one function and not two.
 */
export function captureThumbnailAt(
  width: number,
  height: number,
  type = 'image/png',
  quality?: number,
): string | null {
  const grid = chartLayout.value === 'grid' ? runtime.gridEl : null;
  const source = grid ? null : runtime.rootEl?.querySelector('canvas') ?? null;
  if (!grid && (!source || !source.width || !source.height)) return null;

  const target = document.createElement('canvas');
  target.width = width;
  target.height = height;
  const ctx = target.getContext('2d');
  if (!ctx) return null;

  // The card sits on the app surface, and a plot canvas is transparent where it
  // has not drawn. Without a ground the preview would show whatever is behind
  // the card, which in dark mode is a chart drawn in near-invisible ink.
  const surface = getComputedStyle(document.body).getPropertyValue('--surface').trim();
  ctx.fillStyle = surface || '#111418';
  ctx.fillRect(0, 0, width, height);

  try {
    ctx.imageSmoothingQuality = 'high';
    if (grid) {
      if (!drawGridThumbnail(ctx, grid, width, height)) return null;
      return target.toDataURL(type, quality);
    }
    if (!source) return null;
    // Contain rather than stretch: a squashed chart is harder to recognise than
    // a small one, which is the whole point of the picture.
    const scale = Math.min(width / source.width, height / source.height);
    const drawnWidth = source.width * scale;
    const drawnHeight = source.height * scale;
    ctx.drawImage(
      source,
      (width - drawnWidth) / 2,
      (height - drawnHeight) / 2,
      drawnWidth,
      drawnHeight,
    );
    return target.toDataURL(type, quality);
  } catch {
    // A tainted canvas (a cross-origin map tile drawn into it) cannot be read.
    return null;
  }
}

/** Copy the visible panel layout, including ship names that live outside uPlot's canvases. */
function drawGridThumbnail(
  ctx: CanvasRenderingContext2D,
  grid: HTMLElement,
  width: number,
  height: number,
): boolean {
  const bounds = grid.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return false;
  const panels = [...grid.querySelectorAll<HTMLElement>('.chart-grid-panel')];
  if (!panels.some((panel) => {
    const canvas = panel.querySelector('canvas');
    return canvas && canvas.width && canvas.height;
  })) return false;

  const scale = Math.min(width / bounds.width, height / bounds.height);
  ctx.save();
  ctx.translate((width - bounds.width * scale) / 2, (height - bounds.height * scale) / 2);
  ctx.scale(scale, scale);
  for (const panel of panels) {
    const box = panel.getBoundingClientRect();
    const x = box.left - bounds.left;
    const y = box.top - bounds.top;
    const border = getComputedStyle(panel).borderColor;
    ctx.strokeStyle = border;
    ctx.lineWidth = 1 / scale;
    ctx.strokeRect(x, y, box.width, box.height);

    const title = panel.querySelector<HTMLElement>('.chart-grid-title');
    if (title?.textContent) {
      const titleBox = title.getBoundingClientRect();
      const style = getComputedStyle(title);
      ctx.fillStyle = style.color;
      // A title sized for a full-width panel becomes unreadable after shrinking
      // the grid into a card. Keep the label at least 10 output pixels high.
      const fontSize = Math.max(parseFloat(style.fontSize), 10 / scale);
      ctx.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
      ctx.textBaseline = 'top';
      ctx.fillText(
        title.textContent,
        titleBox.left - bounds.left,
        titleBox.top - bounds.top,
        titleBox.width,
      );
    }

    const canvas = panel.querySelector('canvas');
    if (canvas?.width && canvas.height) {
      const canvasBox = canvas.getBoundingClientRect();
      ctx.drawImage(
        canvas,
        canvasBox.left - bounds.left,
        canvasBox.top - bounds.top,
        canvasBox.width,
        canvasBox.height,
      );
    }
  }
  ctx.restore();
  return true;
}

/**
 * Capture the current chart and store it against `chartId`.
 *
 * Returns whether a preview was stored. Callers treat false as "no picture",
 * never as a failed save - the chart is already saved by the time this runs.
 */
export async function captureAndStore(chartId: string, version: number): Promise<boolean> {
  const dataUrl = captureThumbnail();
  if (!dataUrl) return false;
  try {
    await putChartThumbnail(chartId, {
      data_url: dataUrl,
      width: THUMBNAIL_WIDTH,
      height: THUMBNAIL_HEIGHT,
      version,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Object URLs for previews already fetched, keyed by chart id and version.
 *
 * The image endpoint needs the bearer token, so a card cannot simply point an
 * `<img src>` at it - the request has to go through the API client and become a
 * blob. Keeping the object URL means scrolling back to a card does not re-fetch
 * it, and keying on the version means a re-saved chart gets its new picture
 * rather than the cached old one.
 */
const cache = new Map<string, string>();

export function cachedThumbnail(chartId: string, version: number): string | undefined {
  return cache.get(`${chartId}:${version}`);
}

export function rememberThumbnail(chartId: string, version: number, objectUrl: string): void {
  const key = `${chartId}:${version}`;
  const previous = cache.get(key);
  if (previous && previous !== objectUrl) URL.revokeObjectURL(previous);
  cache.set(key, objectUrl);
}

/** Drop a chart's cached previews - after a delete, or a re-capture. */
export function forgetThumbnails(chartId: string): void {
  for (const [key, url] of cache) {
    if (key.startsWith(`${chartId}:`)) {
      URL.revokeObjectURL(url);
      cache.delete(key);
    }
  }
}
